/*
  Autogenerate for every evidence form. Values are SYNTHETIC — a hard rule — and nothing here
  lands in `rsys_pii`: a case title, a description and a reason are ordinary text in `rsys_`.
  Foreign keys are resolved against the LIVE catalogs the form already holds, so each generator
  takes them as arguments; a parameterless generator would silently fill none of them.

  FILES are generated as a synthetic PDF, because PDF HAS a textual form the server's magic-byte
  check honestly accepts — the rule is "never a binary with no textual format", and without a
  file the primary action could never enable, which is the contract Autogenerate exists for.
  The document says what it is, so nobody mistakes it for real evidence.
*/

import { toDateTimeLocal } from '../../../../lib/formatDate';

export function samplePdf(name: string): File {
  const body = `%PDF-1.4\n% Synthetic document created by Autogenerate - not evidence.\n%%EOF\n`;
  return new File([body], name, { type: 'application/pdf' });
}

function uid(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

function pick<T>(values: readonly T[]): T | undefined {
  return values.length === 0 ? undefined : values[Math.floor(Math.random() * values.length)];
}

export function sampleEvidenceCase() {
  const suffix = uid();

  return {
    caseNumber: `TEST-CASE-${suffix}`,
    title: `Sample investigation ${suffix}`,
  };
}

export function sampleStorageLocation() {
  const suffix = uid();

  return {
    name: `Sample evidence room ${suffix}`,
    description: 'Synthetic location created by Autogenerate.',
  };
}

const ITEMS = [
  'One mobile phone, black, with cracked screen',
  'One laptop computer with charger',
  'Two USB flash drives, 32 GB',
  'One notebook with handwritten entries',
  'One bundle of receipts, 14 sheets',
] as const;

/** A `datetime-local` value some hours before now, in the reader's own zone. */
function hoursAgo(hours: number): string {
  return toDateTimeLocal(new Date(Date.now() - hours * 3_600_000));
}

/**
 * A receiving event: the header and TWO rows — one with an evidence file and one without, so the
 * server derives both kinds. Every row gets a synthetic document; a file row its file.
 */
export function sampleIntake(catalogs: {
  caseIds: string[];
  staffIds: string[];
  typeIds: string[];
}) {
  const suffix = uid();

  const row = (index: number) => ({
    evidenceTypeId: pick(catalogs.typeIds) ?? '',
    description: `${pick(ITEMS) ?? ITEMS[0]} (sample ${suffix}-${index + 1})`,
    acquiredAt: hoursAgo(2 + index),
    agentPersonnelId: pick(catalogs.staffIds) ?? '',
  });

  return {
    evidenceCaseId: pick(catalogs.caseIds) ?? '',
    custodianPersonnelId: pick(catalogs.staffIds) ?? '',
    submittingAgentPersonnelId: pick(catalogs.staffIds) ?? '',
    rows: [row(0), row(1)],
  };
}

/** An evaluation — both halves filled, so either decision submits. */
export function sampleEvaluation(catalogs: { locationIds: string[]; staffIds: string[] }) {
  const suffix = uid();

  return {
    evaluatedByPersonnelId: pick(catalogs.staffIds) ?? '',
    evaluatedAt: hoursAgo(1),
    storedByPersonnelId: pick(catalogs.staffIds) ?? '',
    storedAt: hoursAgo(0.5),
    containerNumber: `SAMPLE-BOX-${suffix}`,
    storageLocationId: pick(catalogs.locationIds) ?? '',
    reason: 'Too large for the evidence room',
    place: `Sample impound yard ${suffix}`,
  };
}

export function sampleMovement(catalogs: { staffIds: string[]; locationIds: string[] }) {
  const suffix = uid();

  return {
    toLocationId: pick(catalogs.locationIds) ?? '',
    toCustodianPersonnelId: pick(catalogs.staffIds) ?? '',
    transferredTo: `Sample receiving office ${suffix}`,
    reason: 'Sample movement',
    justification: `Sample justification ${suffix} — synthetic record created by Autogenerate.`,
  };
}

export function sampleStoreIn(catalogs: { staffIds: string[]; locationIds: string[] }) {
  const suffix = uid();

  return {
    custodianPersonnelId: pick(catalogs.staffIds) ?? '',
    storageLocationId: pick(catalogs.locationIds) ?? '',
    reason: 'Returned to storage',
    justification: `Sample justification ${suffix} — synthetic record created by Autogenerate.`,
  };
}

export function sampleVocabulary(noun: string) {
  const suffix = uid();

  return {
    name: `Sample ${noun} ${suffix}`,
    description: `Synthetic ${noun} created by Autogenerate.`,
  };
}

/** An acknowledgement receipt — the people, a time just past, and every item on offer. */
export function sampleReceipt(catalogs: { staffIds: string[]; itemIds: string[] }) {
  return {
    receivedFromPersonnelId: pick(catalogs.staffIds) ?? '',
    receivedByPersonnelId: pick(catalogs.staffIds) ?? '',
    receivedAt: hoursAgo(1),
    itemIds: catalogs.itemIds,
  };
}

/** A transfer — every destination's fields filled, so whichever is picked submits. The judge is obviously synthetic. */
export function sampleTransfer(catalogs: { itemIds: string[]; disposalTypeIds: string[] }) {
  const suffix = uid();

  return {
    itemIds: catalogs.itemIds,
    courtBranch: `Sample RTC Branch ${suffix}`,
    courtOrderNumber: `SAMPLE-CO-${suffix}`,
    // Lands in rsys_pii — must not look like a person.
    judgeFirstName: 'Placeholder',
    judgeMiddleName: '',
    judgeLastName: 'Testcase',
    judgeSuffix: '',
    disposalTypeId: pick(catalogs.disposalTypeIds) ?? '',
    reason: `Sample reason ${suffix}`,
    place: `Sample impound yard ${suffix}`,
  };
}
